import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, PlusIcon, SearchIcon, LayoutGridIcon, AlertCircleIcon, ArrowLeftIcon, UploadCloudIcon, CalendarPlusIcon, EyeOffIcon, EyeIcon, Trash2Icon, XIcon, ChevronDownIcon, CheckIcon, LayersIcon } from 'lucide-react';
import { useBusiness, CATALOG_LABEL } from './BusinessContext';
import { CatalogItemModal } from './CatalogItemModal';
import { CatalogChat } from './CatalogChat';
import { UpgradePlanModal } from './UpgradePlanModal';
const DEMO_ITEMS = [{
  id: '1',
  title: 'Toyota Prado GXR 2018',
  category: 'SUVs',
  price: 'AED 350/day',
  status: 'Available',
  talabPublished: true,
  aiScore: 92,
  views: 1240,
  rules: 'Delivery configured',
  leads: 18,
  validityDays: 12,
  totalValidity: 30,
  image: 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=400'
}, {
  id: '2',
  title: 'Nissan Patrol 2020',
  category: 'SUVs',
  price: 'AED 450/day',
  status: 'Available',
  talabPublished: true,
  aiScore: 76,
  views: 980,
  rules: 'Missing airport pickup rule',
  leads: 24,
  validityDays: 5,
  totalValidity: 30,
  alert: true,
  image: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=400'
}, {
  id: '3',
  title: 'Toyota Yaris 2022',
  category: 'Hatchbacks',
  price: 'AED 120/day',
  status: 'Inventory',
  talabPublished: false,
  aiScore: 41,
  views: 312,
  rules: 'Needs pricing rules',
  leads: 0,
  validityDays: 30,
  totalValidity: 30,
  alert: true,
  image: 'https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=400'
}];
export function BusinessCatalog() {
  const {
    hub,
    canAddListing,
    currentPlanId,
    listingCount,
    publishListings,
    unpublishListings,
    PACKAGES
  } = useBusiness();
  const catalogName = CATALOG_LABEL[hub?.type ?? 'generic'] || 'Catalog';
  const [showChat, setShowChat] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showCategorySheet, setShowCategorySheet] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const currentPlan = PACKAGES.find(p => p.id === currentPlanId) || PACKAGES[0];
  const limitLabel = currentPlan.listingLimit === Infinity ? 'Unlimited' : currentPlan.listingLimit;
  const [viewMode, setViewMode] = useState<'card' | 'ai'>('card');
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeCategory, setActiveCategory] = useState('All Categories');
  const [talabFilter, setTalabFilter] = useState<'talab' | 'inventory'>('talab');
  const filters = ['All', 'Active', 'Draft', 'Needs AI Rules'];
  const categories = ['All Categories', 'SUVs', 'Sedans', 'Hatchbacks'];
  const [items, setItems] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const categoryItems = items.filter(item => activeCategory === 'All Categories' || item.category === activeCategory);
  const filteredItems = categoryItems.filter(item => talabFilter === 'talab' && item.talabPublished || talabFilter === 'inventory' && !item.talabPublished);
  const offTalabItems = filteredItems.filter(item => !item.talabPublished);
  const onTalabItems = filteredItems.filter(item => item.talabPublished);
  const selectedOffTalab = items.filter(item => selectedItems.has(item.id) && !item.talabPublished);
  const selectedOnTalab = items.filter(item => selectedItems.has(item.id) && item.talabPublished);
  const allOffTalabSelected = offTalabItems.length > 0 && offTalabItems.every(item => selectedItems.has(item.id));
  const allOnTalabSelected = onTalabItems.length > 0 && onTalabItems.every(item => selectedItems.has(item.id));
  const toggleSelectAllOffTalab = () => {
    const newSelected = new Set(selectedItems);
    if (allOffTalabSelected) {
      offTalabItems.forEach(item => newSelected.delete(item.id));
    } else {
      offTalabItems.forEach(item => newSelected.add(item.id));
    }
    setSelectedItems(newSelected);
  };
  const toggleSelectAllOnTalab = () => {
    const newSelected = new Set(selectedItems);
    if (allOnTalabSelected) {
      onTalabItems.forEach(item => newSelected.delete(item.id));
    } else {
      onTalabItems.forEach(item => newSelected.add(item.id));
    }
    setSelectedItems(newSelected);
  };
  const toggleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };
  const handlePublishSelected = () => {
    const ids = selectedOffTalab.map(item => item.id);
    if (ids.length === 0) return;
    if (listingCount + ids.length > currentPlan.listingLimit) {
      setShowUpgrade(true);
      return;
    }
    setItems(prev => prev.map(item => ids.includes(item.id) ? {
      ...item,
      talabPublished: true,
      validityDays: 30,
      totalValidity: 30
    } : item));
    publishListings(ids.length);
    setSelectedItems(new Set());
  };
  const handleUnpublishSelected = () => {
    const ids = selectedOnTalab.map(item => item.id);
    if (ids.length === 0) return;
    setItems(prev => prev.map(item => ids.includes(item.id) ? {
      ...item,
      talabPublished: false,
      status: 'Inventory'
    } : item));
    unpublishListings(ids.length);
    setSelectedItems(new Set());
  };
  const handleExtendSelected = () => {
    const ids = selectedOnTalab.map(item => item.id);
    if (ids.length === 0) return;
    setItems(prev => prev.map(item => ids.includes(item.id) ? {
      ...item,
      validityDays: item.validityDays + 30,
      totalValidity: Math.max(item.totalValidity, item.validityDays + 30)
    } : item));
    setSelectedItems(new Set());
  };
  const handleDeleteSelected = () => {
    const ids = Array.from(selectedItems);
    if (ids.length === 0) return;
    // Keep the plan listing count accurate for any published items removed.
    const publishedRemoved = items.filter(item => selectedItems.has(item.id) && item.talabPublished).length;
    if (publishedRemoved > 0) unpublishListings(publishedRemoved);
    setItems(prev => prev.filter(item => !selectedItems.has(item.id)));
    setSelectedItems(new Set());
    setConfirmDelete(false);
  };
  const handleChatComplete = () => {
    // Simulate the AI adding items to the catalogue.
    setItems(prev => prev.length === 0 ? DEMO_ITEMS : prev);
    setShowChat(false);
  };
  const handleAddInventory = () => {
    if (!canAddListing) {
      setShowUpgrade(true);
    } else {
      setShowChat(true);
    }
  };
  return <div className="w-full h-full flex flex-col" data-id="element-429">
      <div className="space-y-5 pb-24" data-id="element-430">
        {/* Search */}
        <div className="relative" data-id="element-431">
          <SearchIcon className="w-[18px] h-[18px] text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-432" />
          <input type="text" placeholder="Search your catalogue" className="w-full pl-11 pr-4 py-3.5 bg-white border border-slate-200/80 rounded-2xl text-[15px] text-slate-700 placeholder:text-slate-400 shadow-[0_1px_3px_rgb(0,0,0,0.03)] focus:outline-none focus:ring-2 focus:ring-primary-indigo/15 focus:border-primary-indigo/40 transition-all" data-id="element-433" />
        </div>

        {/* Category filter + Add */}
        <div className="flex items-center gap-2.5" data-id="element-434">
          <button onClick={() => setShowCategorySheet(true)} aria-haspopup="dialog" aria-expanded={showCategorySheet} className="flex-1 min-w-0 flex items-center gap-2 px-3.5 py-2.5 bg-white border border-slate-200/80 rounded-2xl text-left shadow-[0_1px_3px_rgb(0,0,0,0.03)] active:bg-slate-50 transition-colors" data-id="element-435">
            <LayersIcon className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-436" />
            <span className="flex flex-col min-w-0" data-id="element-437">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider leading-none mb-0.5" data-id="element-438">
                Category
              </span>
              <span className="text-[13px] font-semibold text-dark-navy truncate leading-tight" data-id="element-439">
                {activeCategory}
              </span>
            </span>
            <ChevronDownIcon className="w-4 h-4 text-slate-400 flex-shrink-0 ml-auto" data-id="element-440" />
          </button>
          <button onClick={handleAddInventory} className="flex-shrink-0 flex items-center gap-1.5 px-4 py-3 bg-primary-indigo text-white text-sm font-bold rounded-2xl shadow-[0_4px_12px_rgb(79,70,229,0.25)] active:scale-95 transition-transform" data-id="element-441">
            <PlusIcon className="w-[18px] h-[18px]" data-id="element-442" /> Add Item
          </button>
        </div>

        {/* Talab publication segmented control */}
        <div className="flex bg-slate-100 p-1 rounded-2xl" data-id="element-443">
          {([{
          id: 'talab',
          label: 'Published',
          count: categoryItems.filter(i => i.talabPublished).length
        }, {
          id: 'inventory',
          label: 'Inventory',
          count: categoryItems.filter(i => !i.talabPublished).length
        }] as const).map(seg => <button key={seg.id} onClick={() => setTalabFilter(seg.id)} className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${talabFilter === seg.id ? 'bg-white text-primary-indigo shadow-sm' : 'text-slate-500 active:text-slate-700'}`} data-id="element-444">
              {seg.label}
              <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md ${talabFilter === seg.id ? 'bg-primary-indigo/10 text-primary-indigo' : 'bg-slate-200/70 text-slate-500'}`} data-id="element-445">
                {seg.count}
              </span>
            </button>)}
        </div>

        {/* Item list */}
        {items.length === 0 ? <div className="flex flex-col items-center text-center px-6 py-12 bg-white border border-dashed border-slate-200 rounded-3xl" data-id="element-446">
            <div className="w-16 h-16 rounded-2xl bg-primary-indigo/5 flex items-center justify-center mb-4" data-id="element-447">
              <LayersIcon className="w-7 h-7 text-primary-indigo" data-id="element-448" />
            </div>
            <h3 className="text-base font-bold text-dark-navy mb-1" data-id="element-449">
              Your catalogue is empty
            </h3>
            <p className="text-sm text-slate-500 max-w-[260px] mb-5" data-id="element-450">
              Add your first item and Talab AI will help you build a polished
              listing in seconds.
            </p>
            <button onClick={handleAddInventory} className="inline-flex items-center gap-1.5 px-5 py-3 bg-primary-indigo text-white text-sm font-bold rounded-2xl shadow-[0_4px_12px_rgb(79,70,229,0.25)] active:scale-95 transition-transform" data-id="element-451">
              <PlusIcon className="w-[18px] h-[18px]" data-id="element-452" /> Add your first item
            </button>
          </div> : <div className="flex flex-col gap-4" data-id="element-453">
            {filteredItems.map(item => <CatalogItemCard key={item.id} {...item} selectionMode={selectedItems.size > 0} selected={selectedItems.has(item.id)} onToggleSelect={() => toggleSelectItem(item.id)} onOpen={() => setSelectedItem(item.title)} data-id="element-454" />)}
            {filteredItems.length === 0 && <div className="py-12 text-center text-sm font-medium text-slate-400 bg-white border border-dashed border-slate-200 rounded-2xl" data-id="element-455">
                No items in this view.
              </div>}
          </div>}
      </div>

      {/* Item detail */}
      {selectedItem && <CatalogItemModal itemTitle={selectedItem} onClose={() => setSelectedItem(null)} data-id="element-456" />}

      {showUpgrade && <UpgradePlanModal onClose={() => setShowUpgrade(false)} data-id="element-457" />}

      <CategorySheet open={showCategorySheet} categories={categories} active={activeCategory} onSelect={c => {
      setActiveCategory(c);
      setShowCategorySheet(false);
    }} onClose={() => setShowCategorySheet(false)} data-id="element-458" />

      <AddItemScreen open={showChat} catalogName={catalogName} onClose={() => setShowChat(false)} onComplete={handleChatComplete} data-id="element-459" />

      <AnimatePresence data-id="element-460">
        {selectedItems.size > 0 && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="absolute bottom-6 left-4 right-4 bg-dark-navy text-white p-4 rounded-2xl shadow-2xl flex flex-col gap-3 z-50" data-id="element-461">
            {confirmDelete ? <>
                <div className="flex items-start gap-2.5" data-id="element-462">
                  <div className="w-9 h-9 rounded-xl bg-red-500/20 flex items-center justify-center shrink-0" data-id="element-463">
                    <Trash2Icon className="w-4.5 h-4.5 text-red-300" data-id="element-464" />
                  </div>
                  <div className="flex-1" data-id="element-465">
                    <p className="font-bold text-sm" data-id="element-466">
                      Delete {selectedItems.size}{' '}
                      {selectedItems.size === 1 ? 'item' : 'items'}?
                    </p>
                    <p className="text-xs text-white/60 mt-0.5" data-id="element-467">
                      This permanently removes them from your catalogue.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-468">
                  <button onClick={() => setConfirmDelete(false)} className="flex-1 py-2.5 bg-white/10 active:bg-white/20 text-white text-xs font-bold rounded-xl" data-id="element-469">
                    Cancel
                  </button>
                  <button onClick={handleDeleteSelected} className="flex-1 py-2.5 bg-red-600 active:bg-red-700 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1.5" data-id="element-470">
                    <Trash2Icon className="w-3.5 h-3.5" data-id="element-471" /> Delete
                  </button>
                </div>
              </> : confirmUnpublish ? <>
                <div className="flex items-start gap-2.5" data-id="element-472">
                  <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0" data-id="element-473">
                    <EyeOffIcon className="w-4.5 h-4.5 text-white/80" data-id="element-474" />
                  </div>
                  <div className="flex-1" data-id="element-475">
                    <p className="font-bold text-sm" data-id="element-476">
                      Remove {selectedOnTalab.length}{' '}
                      {selectedOnTalab.length === 1 ? 'item' : 'items'} from
                      Talab?
                    </p>
                    <p className="text-xs text-white/60 mt-0.5" data-id="element-477">
                      Unpublish keeps them in your inventory. Delete removes
                      them entirely.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2" data-id="element-478">
                  <div className="flex gap-2" data-id="element-479">
                    <button onClick={() => {
                handleUnpublishSelected();
                setConfirmUnpublish(false);
              }} className="flex-1 py-2.5 bg-white text-dark-navy text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 active:bg-slate-100" data-id="element-480">
                      <EyeOffIcon className="w-3.5 h-3.5" data-id="element-481" /> Unpublish only
                    </button>
                    <button onClick={() => {
                handleDeleteSelected();
                setConfirmUnpublish(false);
              }} className="flex-1 py-2.5 bg-red-600 active:bg-red-700 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1.5" data-id="element-482">
                      <Trash2Icon className="w-3.5 h-3.5" data-id="element-483" /> Delete
                    </button>
                  </div>
                  <button onClick={() => setConfirmUnpublish(false)} className="w-full py-2.5 bg-white/10 active:bg-white/20 text-white text-xs font-bold rounded-xl" data-id="element-484">
                    Cancel
                  </button>
                </div>
              </> : <>
                <div className="flex items-center justify-between" data-id="element-485">
                  <div className="flex items-center gap-2 font-bold text-sm" data-id="element-486">
                    <span className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-xs" data-id="element-487">
                      {selectedItems.size}
                    </span>
                    selected
                  </div>
                  <div className="flex items-center gap-3" data-id="element-488">
                    <button onClick={() => {
                const allSelected = filteredItems.length > 0 && filteredItems.every(i => selectedItems.has(i.id));
                if (allSelected) {
                  setSelectedItems(new Set());
                } else {
                  setSelectedItems(new Set(filteredItems.map(i => i.id)));
                }
              }} className="text-xs font-bold text-white active:text-slate-300" data-id="element-489">
                      {filteredItems.length > 0 && filteredItems.every(i => selectedItems.has(i.id)) ? 'Deselect all' : 'Select all'}
                    </button>
                    <span className="w-px h-3 bg-white/20" data-id="element-490" />
                    <button onClick={() => {
                setSelectedItems(new Set());
                setConfirmDelete(false);
                setConfirmUnpublish(false);
              }} className="text-xs font-bold text-slate-300 active:text-white" data-id="element-491">
                      Clear
                    </button>
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-492">
                  {selectedOnTalab.length > 0 && <>
                      <button onClick={handleExtendSelected} className="flex-1 py-2.5 bg-white/10 active:bg-white/20 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1.5" data-id="element-493">
                        <CalendarPlusIcon className="w-3.5 h-3.5" data-id="element-494" /> Extend
                      </button>
                      <button onClick={() => setConfirmUnpublish(true)} className="flex-1 py-2.5 bg-white/10 active:bg-red-500/80 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1.5" data-id="element-495">
                        <EyeOffIcon className="w-3.5 h-3.5" data-id="element-496" /> Unpublish
                      </button>
                    </>}
                  {selectedOffTalab.length > 0 && <button onClick={handlePublishSelected} className="flex-1 py-2.5 bg-primary-indigo active:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-sm flex items-center justify-center gap-1.5" data-id="element-497">
                      <UploadCloudIcon className="w-3.5 h-3.5" data-id="element-498" /> Publish
                    </button>}
                  <button onClick={() => setConfirmDelete(true)} className="flex-1 py-2.5 bg-white/10 active:bg-red-600 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1.5" data-id="element-499">
                    <Trash2Icon className="w-3.5 h-3.5" data-id="element-500" /> Delete
                  </button>
                </div>
              </>}
          </motion.div>}
      </AnimatePresence>

      <SimulateContentControl populated={items.length > 0} onToggle={() => {
      setItems(prev => prev.length > 0 ? [] : DEMO_ITEMS);
      setShowChat(false);
      setSelectedItems(new Set());
    }} data-id="element-501" />
    </div>;
}
function SimulateContentControl({
  populated,
  onToggle
}: {
  populated: boolean;
  onToggle: () => void;
}) {
  // Mounts OUTSIDE the phone frame (on document.body), so it never appears
  // inside the app — it's a preview helper to simulate catalogue content.
  if (typeof document === 'undefined') return null;
  return createPortal(<button onClick={onToggle} className="fixed bottom-5 right-5 z-[9999] flex items-center gap-2 px-4 py-2.5 bg-dark-navy text-white text-xs font-bold rounded-full shadow-xl border border-white/10 hover:bg-slate-800 active:scale-95 transition-all" data-id="element-502">
      <LayersIcon className="w-4 h-4" data-id="element-503" />
      {populated ? 'Reset to empty catalogue' : 'Simulate catalogue content'}
    </button>, document.body);
}
function KpiCard({
  title,
  value,
  trend,
  alert,
  outOf,
  onClick
}: any) {
  const Card = onClick ? 'button' : 'div';
  return <Card onClick={onClick} className={`p-4 bg-white border rounded-2xl flex flex-col w-full ${alert ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200/60 shadow-sm'} ${onClick ? 'active:scale-95 transition-transform text-left' : ''}`} data-id="element-504">
      <span className="text-xs font-semibold text-slate-500 mb-1.5" data-id="element-505">
        {title}
      </span>
      <div className="flex items-end gap-2" data-id="element-506">
        <span className={`text-2xl font-display font-bold leading-none ${alert ? 'text-amber-600' : 'text-dark-navy'}`} data-id="element-507">
          {value}
          {outOf && <span className="text-base font-bold text-slate-400" data-id="element-508">
              {' '}
              / {outOf}
            </span>}
        </span>
        {trend && <span className="text-[10px] font-bold text-primary-indigo mb-0.5 bg-indigo-50 px-1.5 py-0.5 rounded-md" data-id="element-509">
            {trend}
          </span>}
      </div>
      {outOf && onClick && <span className="text-[10px] font-bold text-primary-indigo mt-1.5" data-id="element-510">
          Upgrade for more
        </span>}
    </Card>;
}
function formatValidUntil(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
function CatalogItemCard({
  title,
  category,
  price,
  status,
  talabPublished = true,
  aiScore,
  views,
  rules,
  leads,
  image,
  validityDays = 30,
  totalValidity = 30,
  alert,
  selectionMode,
  selected,
  onToggleSelect,
  onOpen
}: any) {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const startPress = () => {
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      // Haptic feedback where supported.
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        try {
          navigator.vibrate(15);
        } catch {}
      }
      onToggleSelect();
    }, 380);
  };
  const cancelPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  const handleClick = () => {
    // If a long-press already toggled selection, swallow the click.
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    if (selectionMode) {
      onToggleSelect();
    } else {
      onOpen();
    }
  };
  return <div className={`bg-white border rounded-2xl overflow-hidden shadow-sm active:scale-[0.98] transition-transform flex flex-col relative select-none ${selected ? 'border-primary-indigo ring-2 ring-primary-indigo' : 'border-slate-200/60'}`} onClick={handleClick} onTouchStart={startPress} onTouchEnd={cancelPress} onTouchMove={cancelPress} onMouseDown={startPress} onMouseUp={cancelPress} onMouseLeave={cancelPress} onContextMenu={e => e.preventDefault()} data-id="element-511">
      {selectionMode && <div className="absolute top-3 left-3 z-20" data-id="element-512">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center shadow-sm transition-colors ${selected ? 'bg-primary-indigo' : 'bg-white/90 backdrop-blur-sm border border-slate-300'}`} data-id="element-513">
            {selected && <CheckIcon className="w-4 h-4 text-white" data-id="element-514" />}
          </div>
        </div>}
      {selected && <div className="absolute inset-0 z-10 bg-primary-indigo/5 pointer-events-none" data-id="element-515" />}
      <div className="h-36 bg-slate-100 relative overflow-hidden" data-id="element-516">
        <img src={image} alt={title} className="w-full h-full object-cover" data-id="element-517" />
        <div className="absolute top-3 right-3 px-2 py-1 bg-white/95 backdrop-blur-sm rounded-md text-[10px] font-bold text-dark-navy shadow-sm uppercase tracking-wider" data-id="element-518">
          {status}
        </div>
        {category && <div className="absolute top-3 left-11 px-2 py-1 bg-white/95 backdrop-blur-sm rounded-md text-[10px] font-bold text-primary-indigo shadow-sm uppercase tracking-wider" data-id="element-519">
            {category}
          </div>}
        <div className={`absolute bottom-3 left-3 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider shadow-sm ${talabPublished ? 'bg-green-600 text-white' : 'bg-slate-700 text-white'}`} data-id="element-520">
          {talabPublished ? 'On Talab' : 'Widget only'}
        </div>
      </div>
      <div className="p-4 flex flex-col gap-3" data-id="element-521">
        <div data-id="element-522">
          <h4 className="font-bold text-dark-navy text-sm mb-0.5 truncate" data-id="element-523">
            {title}
          </h4>
          <div className="text-primary-indigo font-bold text-sm" data-id="element-524">{price}</div>
        </div>

        <div className="grid grid-cols-2 gap-2 bg-slate-50 p-2.5 rounded-xl" data-id="element-525">
          <div className="flex flex-col gap-0.5" data-id="element-526">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1" data-id="element-527">
              <EyeIcon className="w-3 h-3 text-primary-indigo" data-id="element-528" /> Views
            </span>
            <span className="text-xs font-bold text-dark-navy" data-id="element-529">
              {(views ?? 0).toLocaleString()}
            </span>
          </div>
          <div className="flex flex-col gap-0.5 pl-2 border-l border-slate-200" data-id="element-530">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-531">
              Leads
            </span>
            <span className="text-xs font-bold text-dark-navy" data-id="element-532">{leads}</span>
          </div>
        </div>

        <div className="pt-1" data-id="element-533">
          {talabPublished ? <div className="flex flex-col gap-1.5" data-id="element-534">
              <div className="flex items-center justify-between text-[11px] font-semibold" data-id="element-535">
                <span className="text-slate-500 uppercase tracking-wider" data-id="element-536">
                  Talab validity
                </span>
                <span className={validityDays < 7 ? 'text-amber-600' : 'text-slate-700'} data-id="element-537">
                  {validityDays} days left
                </span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-538">
                <div className={`h-full transition-all ${validityDays < 7 ? 'bg-amber-500' : 'bg-primary-indigo'}`} style={{
              width: `${validityDays / totalValidity * 100}%`
            }} data-id="element-539" />
              </div>
            </div> : <div className="flex items-center justify-between text-[11px] font-semibold" data-id="element-540">
              <span className="text-slate-500 uppercase tracking-wider" data-id="element-541">
                Status
              </span>
              <span className="text-slate-700" data-id="element-542">
                In inventory · live on widget
              </span>
            </div>}
        </div>
      </div>
    </div>;
}
function AIReviewGroup({
  title,
  count,
  items,
  action
}: any) {
  return <div className="bg-white border border-slate-200/60 rounded-2xl p-4 shadow-sm flex flex-col gap-4" data-id="element-543">
      <div className="flex items-center justify-between" data-id="element-544">
        <div className="flex items-center gap-2" data-id="element-545">
          <AlertCircleIcon className="w-5 h-5 text-amber-500" data-id="element-546" />
          <h4 className="font-bold text-dark-navy text-sm" data-id="element-547">{title}</h4>
        </div>
        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider rounded-md" data-id="element-548">
          {count} items
        </span>
      </div>
      <div className="flex flex-col gap-2" data-id="element-549">
        {items.map((item: string, i: number) => <div key={i} className="text-xs font-semibold text-slate-700 flex items-center justify-between p-2.5 bg-slate-50 rounded-xl border border-slate-100" data-id="element-550">
            {item}
            <button className="text-primary-indigo active:text-indigo-700 font-bold" data-id="element-551">
              Review
            </button>
          </div>)}
      </div>
      <button className="w-full py-2.5 bg-primary-indigo/5 text-primary-indigo text-xs font-bold rounded-xl active:bg-primary-indigo/10" data-id="element-552">
        {action} for all {count}
      </button>
    </div>;
}
function CategorySheet({
  open,
  categories,
  active,
  onSelect,
  onClose
}: {
  open: boolean;
  categories: string[];
  active: string;
  onSelect: (c: string) => void;
  onClose: () => void;
}) {
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<AnimatePresence data-id="element-553">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 z-[110] flex flex-col justify-end" data-id="element-554">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={onClose} data-id="element-555" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="relative bg-white rounded-t-3xl shadow-2xl flex flex-col max-h-[72%] overflow-hidden" data-id="element-556">
            {/* Grabber */}
            <div className="flex-shrink-0 pt-3 pb-1 flex justify-center" data-id="element-557">
              <div className="w-10 h-1.5 rounded-full bg-slate-200" data-id="element-558" />
            </div>
            {/* Header */}
            <div className="flex-shrink-0 px-5 pt-2 pb-3 flex items-center justify-between border-b border-slate-100" data-id="element-559">
              <div data-id="element-560">
                <h3 className="text-base font-bold text-dark-navy" data-id="element-561">Category</h3>
                <p className="text-xs text-slate-400" data-id="element-562">
                  Filter your inventory by category
                </p>
              </div>
              <button onClick={onClose} aria-label="Close" className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 active:scale-95 transition-transform" data-id="element-563">
                <XIcon className="w-4 h-4" data-id="element-564" />
              </button>
            </div>
            {/* List */}
            <div className="flex-1 overflow-y-auto px-3 py-3" data-id="element-565">
              <div className="flex flex-col gap-1" data-id="element-566">
                {categories.map(c => {
              const isActive = c === active;
              return <button key={c} onClick={() => onSelect(c)} className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-left transition-colors ${isActive ? 'bg-indigo-50' : 'active:bg-slate-50'}`} data-id="element-567">
                      <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-primary-indigo text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-568">
                        <LayersIcon className="w-4 h-4" data-id="element-569" />
                      </span>
                      <span className={`flex-1 text-sm font-semibold truncate ${isActive ? 'text-primary-indigo' : 'text-slate-700'}`} data-id="element-570">
                        {c}
                      </span>
                      {isActive && <CheckIcon className="w-5 h-5 text-primary-indigo flex-shrink-0" data-id="element-571" />}
                    </button>;
            })}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, mountNode);
}
function AddItemScreen({
  open,
  catalogName,
  onClose,
  onComplete
}: {
  open: boolean;
  catalogName: string;
  onClose: () => void;
  onComplete: () => void;
}) {
  const {
    currentPlanId,
    listingCount,
    PACKAGES
  } = useBusiness();
  const freePlan = PACKAGES.find(p => p.id === 'Free');
  const onFreePlan = currentPlanId === 'Free';
  const freeRemaining = Math.max(0, (freePlan?.listingLimit ?? 1) - listingCount);
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<AnimatePresence data-id="element-572">
      {open && <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 28,
      stiffness: 250
    }} className="absolute inset-0 z-[100] bg-[#F8FAFC] flex flex-col overflow-hidden" data-id="element-573">
          {/* Sticky Header */}
          <div className="flex-shrink-0 bg-white border-b border-slate-100 z-20" data-id="element-574">
            <div className="flex items-center gap-2 px-3 py-3" data-id="element-575">
              <button onClick={onClose} aria-label="Back to catalogue" className="p-2 -ml-1 text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-576">
                <ArrowLeftIcon className="w-6 h-6" data-id="element-577" />
              </button>
              <div className="flex-1 min-w-0" data-id="element-578">
                <h2 className="text-lg font-bold text-dark-navy leading-tight" data-id="element-579">
                  Add Item
                </h2>
                <p className="text-xs text-slate-400 truncate" data-id="element-580">
                  Build your listing with Talab AI
                </p>
              </div>
            </div>
            {/* Free plan callout — first plan is free for 1 listing */}
            {onFreePlan && <div className="mx-3 mb-3 flex items-center gap-3 px-3.5 py-2.5 bg-emerald-50 border border-emerald-100 rounded-2xl" data-id="element-581">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0" data-id="element-582">
                  <SparklesIcon className="w-4 h-4 text-emerald-600" data-id="element-583" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-584">
                  <p className="text-[13px] font-bold text-emerald-900 leading-tight" data-id="element-585">
                    Free plan · 1 listing included
                  </p>
                  <p className="text-[11px] text-emerald-700/80 leading-tight mt-0.5" data-id="element-586">
                    {freeRemaining > 0 ? 'Your first listing is on us — no card needed.' : 'Free listing used — upgrade anytime for more.'}
                  </p>
                </div>
                <span className="flex-shrink-0 text-[11px] font-bold text-emerald-700 bg-white/70 border border-emerald-100 px-2 py-1 rounded-lg" data-id="element-587">
                  {freeRemaining}/{freePlan?.listingLimit ?? 1} free
                </span>
              </div>}
          </div>
          {/* Chat body */}
          <div className="flex-1 min-h-0 flex flex-col p-3" data-id="element-588">
            <CatalogChat catalogName={catalogName} onPopulateDemo={onComplete} data-id="element-589" />
          </div>
        </motion.div>}
    </AnimatePresence>, mountNode);
}